import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Download, Plus, Trash2, Loader2, Lock, Chrome, Globe } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '@/lib/api';

const BROWSERS = [
  { id: '', label: 'Só cookies.txt' },
  { id: 'chrome', label: 'Chrome' },
  { id: 'edge', label: 'Edge' },
  { id: 'firefox', label: 'Firefox' },
  { id: 'brave', label: 'Brave' },
  { id: 'opera', label: 'Opera' },
];

export const DownloadAccountManager = ({ masterPassword, credentials, onChanged }) => {
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [label, setLabel] = useState('');
  const [browser, setBrowser] = useState('chrome');
  const [cookies, setCookies] = useState('');

  const reset = () => { setLabel(''); setBrowser('chrome'); setCookies(''); setShowForm(false); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!masterPassword) { toast.error('Desbloqueie o cofre com a senha mestra'); return; }
    if (!browser && !cookies.trim()) {
      toast.error('Escolha um navegador ou cole o cookies.txt');
      return;
    }
    setSaving(true);
    try {
      await api.storeVaultCredential({
        master_password: masterPassword,
        platform: 'download_account',
        username: label || 'download',
        password: '',
        account_label: label || 'Conta de Download',
        additional: { browser, cookies: cookies.trim() },
      });
      toast.success('Conta de download salva com criptografia');
      reset();
      onChanged && onChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar conta de download');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover esta conta de download?')) return;
    try {
      await api.deleteVaultCredential(id, masterPassword);
      toast.success('Conta de download removida');
      onChanged && onChanged();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover');
    }
  };

  return (
    <Card className="border-primary/30" data-testid="download-account-manager">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Download className="w-5 h-5 text-primary" />
          Conta de Download
        </CardTitle>
        <CardDescription>
          Conta do YouTube que o app usa para baixar os vídeos dos clips. Quando o servidor for bloqueado,
          o Desktop Agent baixa no seu PC usando esta conta (navegador logado e/ou cookies.txt) e envia de volta.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {credentials.length > 0 && (
          <div className="space-y-2">
            {credentials.map((cred) => (
              <div key={cred.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`download-account-${cred.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                    <Download className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{cred.account_label || 'Conta de Download'}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {cred.download_browser ? (
                        <Badge variant="outline" className="text-xs"><Chrome className="w-3 h-3 mr-1" />{cred.download_browser}</Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs"><Globe className="w-3 h-3 mr-1" />cookies.txt</Badge>
                      )}
                      <Badge variant="outline" className="text-xs"><Lock className="w-3 h-3 mr-1" />Criptografado</Badge>
                    </div>
                  </div>
                </div>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(cred.id)} data-testid={`download-account-delete-${cred.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {showForm ? (
          <form onSubmit={handleSave} className="space-y-4 p-4 border rounded-lg">
            <div className="space-y-2">
              <Label htmlFor="dl-label">Nome da conta (exibição)</Label>
              <Input
                id="dl-label"
                placeholder="Ex.: Minha conta de download"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                data-testid="download-account-label-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Navegador logado no PC (usado pelo Agent)</Label>
              <div className="grid grid-cols-3 gap-2">
                {BROWSERS.map((b) => (
                  <Button
                    key={b.id || 'none'}
                    type="button"
                    variant={browser === b.id ? 'default' : 'outline'}
                    className="h-auto py-2 text-xs"
                    onClick={() => setBrowser(b.id)}
                    data-testid={`download-account-browser-${b.id || 'none'}`}
                  >
                    {b.label}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                O Agent usa os cookies desse navegador (yt-dlp --cookies-from-browser). Escolha "Só cookies.txt" para usar apenas o texto abaixo.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dl-cookies">cookies.txt (opcional — formato Netscape)</Label>
              <textarea
                id="dl-cookies"
                className="w-full h-28 text-xs font-mono rounded-md border border-border bg-background p-2"
                placeholder="# Netscape HTTP Cookie File ..."
                value={cookies}
                onChange={(e) => setCookies(e.target.value)}
                data-testid="download-account-cookies-textarea"
              />
              <p className="text-xs text-muted-foreground">
                Fallback usado quando o navegador não estiver disponível. Fica criptografado no cofre.
              </p>
            </div>

            <div className="flex gap-2">
              <Button type="submit" disabled={saving} data-testid="download-account-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                <span className="ml-2">Salvar</span>
              </Button>
              <Button type="button" variant="outline" onClick={reset}>Cancelar</Button>
            </div>
          </form>
        ) : (
          <Button variant={credentials.length > 0 ? 'outline' : 'default'} onClick={() => setShowForm(true)} data-testid="download-account-add-btn">
            <Plus className="w-4 h-4 mr-2" />
            {credentials.length > 0 ? 'Adicionar outra conta de download' : 'Adicionar Conta de Download'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
